/**
 * Módulo: Movements API - Interface Pública para Registro de Movimentações
 * Arquivo: js/pages/suprimentos/estoque/movements-api.js
 * 
 * INTERFACE PÚBLICA usada por:
 * - lancamentos-crud.js (financeiro)
 * - maquinas_new.js  
 * - operacional.js
 * 
 * GARANTIAS:
 * - Validação rigorosa
 * - Recálculo automático cronológico
 * - Tratamento de erros robusto
 */

window.FazendaRP = window.FazendaRP || {};
window.FazendaRP.Estoque = window.FazendaRP.Estoque || {};

/**
 * API principal: Registrar movimentação de estoque
 * 
 * @param {Object} params - Parâmetros da movimentação
 * @param {string} params.itemNome - Nome do item (obrigatório)
 * @param {string} [params.itemId] - ID do item no catálogo (opcional)
 * @param {string} params.tipo - 'ENTRADA' ou 'SAIDA' (obrigatório)
 * @param {number} params.quantidade - Quantidade (obrigatório, > 0)
 * @param {number} [params.custoUnitario] - Custo unitário (obrigatório para ENTRADA)
 * @param {string} params.origemTipo - 'COMPRA', 'VENDA', 'APLICACAO', 'MAQUINA', 'MANUAL'
 * @param {string} [params.origemId] - ID do lançamento/abastecimento origem
 * @param {string} [params.observacao] - Observações
 * @param {string} [params.unidade] - Unidade (default: 'UN')
 * @param {string} [params.dataOperacao] - Data da transação (default: hoje)
 * 
 * @returns {Promise<Object>} { success, estoqueId, saldo, custo }
 * 
 * @example
 * await window.FazendaRP.Estoque.registrarMovimentacao({
 *     itemNome: 'Diesel S500',
 *     tipo: 'ENTRADA',
 *     quantidade: 500,
 *     custoUnitario: 5.50,
 *     origemTipo: 'COMPRA',
 *     origemId: lancamentoId,
 *     dataOperacao: '2024-12-01'
 * });
 */
window.FazendaRP.Estoque.registrarMovimentacao = async function (params) {
    console.log('📦 [EstoqueAPI] Registrando movimentação:', params.tipo, params.quantidade, params.itemNome);

    try {
        const DataService = window.FazendaRP.Estoque.DataService;
        const Calculator = window.FazendaRP.Estoque.Calculator;
        const Reconciliation = window.FazendaRP.Estoque.Reconciliation;

        // 1. VALIDAÇÃO
        const validacao = Calculator.validarMovimentacao(params);
        if (!validacao.valid) {
            const erros = validacao.errors.join(', ');
            throw new Error(`Validação falhou: ${erros}`);
        }

        // Validação adicional: ENTRADA precisa de custo
        if (params.tipo === 'ENTRADA') {
            if (!params.custoUnitario || params.custoUnitario === 0) {
                console.warn('[EstoqueAPI] ENTRADA com custo ZERO detectada. Tentando recuperar...');
                // Fallback: If total exists, try to calc
                if (params.valorTotal && params.quantidade > 0) {
                    params.custoUnitario = params.valorTotal / params.quantidade;
                    console.log('[EstoqueAPI] Custo recuperado pelo Total:', params.custoUnitario);
                } else {
                    // HARD STOP: Don't allow zero cost entry for now to force debug
                    // throw new Error('Entrada de Estoque requer Custo Unitário válido (> 0).');
                    // Actually, user might want zero cost. But for this bug, let's warn loudly.
                    console.error('[EstoqueAPI] ALERTA CRÍTICO: Gravando Entrada com Custo ZERO!');
                }
            }
        }

        // 2. BUSCAR OU CRIAR ESTOQUE
        const estoque = await DataService.buscarOuCriarEstoque(
            params.itemNome,
            params.itemId,
            params.unidade || 'UN',
            params.custoUnitario || 0
        );

        // 3. INSERIR MOVIMENTAÇÃO
        await DataService.inserirMovimentacao({
            estoqueId: estoque.id,
            itemNome: estoque.item_nome, // Usar nome normalizado do banco
            tipo: params.tipo,
            quantidade: params.quantidade,
            origemTipo: params.origemTipo,
            origemId: params.origemId,
            observacao: params.observacao || '',
            custo_unitario: parseFloat(params.custoUnitario || 0), // ✅ SNAPSHOT
            valor_total: parseFloat(params.quantidade) * parseFloat(params.custoUnitario || 0), // ✅ SNAPSHOT
            dataOperacao: params.dataOperacao || new Date().toISOString().split('T')[0]
        });

        // 4. RECALCULAR ESTOQUE EM ORDEM CRONOLÓGICA
        console.log(`🔄 [EstoqueAPI] Disparando recálculo cronológico...`);
        const result = await Reconciliation.recalcularEstoque(estoque.id);

        if (!result.success) {
            throw new Error(`Falha no recálculo: ${result.error}`);
        }

        console.log(`✅ [EstoqueAPI] Movimentação registrada com sucesso!`);
        console.log(`   Saldo Final: ${result.saldo.toFixed(2)} | Custo Médio: R$ ${result.custo.toFixed(2)}`);

        return {
            success: true,
            estoqueId: estoque.id,
            saldo: result.saldo,
            custo: result.custo,
            timeline: result.timeline
        };

    } catch (error) {
        console.error('❌ [EstoqueAPI] Erro ao registrar movimentação:', error);
        throw error; // Propagar erro para chamador tratar
    }
};

/**
 * Deleta movimentação manual e recalcula estoque
 * 
 * @param {string} movimentacaoId - ID da movimentação
 * @param {string} estoqueId - ID do estoque
 * @returns {Promise<Object>} { success, saldo, custo }
 */
window.FazendaRP.Estoque.deletarMovimentacao = async function (movimentacaoId, estoqueId) {
    console.log(`🗑️ [EstoqueAPI] Deletando movimentação ${movimentacaoId}`);

    try {
        const DataService = window.FazendaRP.Estoque.DataService;
        const Reconciliation = window.FazendaRP.Estoque.Reconciliation;

        // 1. Deletar movimentação
        await DataService.deletarMovimentacao(movimentacaoId);

        // 2. Recalcular estoque
        const result = await Reconciliation.recalcularEstoque(estoqueId);

        if (!result.success) {
            throw new Error(`Falha no recálculo: ${result.error}`);
        }

        console.log(`✅ [EstoqueAPI] Movimentação deletada e estoque recalculado`);

        return {
            success: true,
            saldo: result.saldo,
            custo: result.custo
        };

    } catch (error) {
        console.error('❌ [EstoqueAPI] Erro ao deletar movimentação:', error);
        throw error;
    }
};

/**
 * Deleta movimentações por origem (ex: ABASTECIMENTO, COMPRA)
 * Útil para exclusão em cascata
 * 
 * @param {string} origemTipo - Tipo de origem (ex: 'ABASTECIMENTO')
 * @param {string|number} origemId - ID da origem (pode ser string para UUID ou number para ID serial)
 * @returns {Promise<Object>} { success, count }
 */
window.FazendaRP.Estoque.deletarMovimentacaoPorOrigem = async function (origemTipo, origemId) {
    console.log(`🗑️ [EstoqueAPI] Deletando movimentações de ${origemTipo} ID ${origemId}`);

    try {
        const Reconciliation = window.FazendaRP.Estoque.Reconciliation;

        // 1. Buscar as movimentações para saber quais estoque_id recalcular
        // Usamos ilike ou eq dependendo da necessidade, mas eq é mais seguro aqui
        const { data: movs, error: fetchErr } = await window.FazendaRP.supabase
            .from('suprimentos_movimentacoes')
            .select('id, estoque_id')
            .eq('origem_tipo', origemTipo)
            .eq('origem_id', origemId);

        if (fetchErr) throw fetchErr;

        if (!movs || movs.length === 0) {
            console.log('⚠️ [EstoqueAPI] Nenhuma movimentação encontrada para esta origem.');
            return { success: true, count: 0 };
        }

        // 2. Coletar estoque_ids únicos para recálculo
        const estoqueIds = [...new Set(movs.map(m => m.estoque_id))];

        // 3. Deletar movimentações
        const { error: delErr } = await window.FazendaRP.supabase
            .from('suprimentos_movimentacoes')
            .delete()
            .eq('origem_tipo', origemTipo)
            .eq('origem_id', origemId);

        if (delErr) throw delErr;

        // 4. Recalcular cada estoque afetado
        console.log(`🔄 [EstoqueAPI] Recalculando ${estoqueIds.length} estoque(s) afetado(s)...`);
        for (const estoqueId of estoqueIds) {
            await Reconciliation.recalcularEstoque(estoqueId);
        }

        console.log(`✅ [EstoqueAPI] ${movs.length} movimentação(ões) deletada(s) e estoque(s) recalculado(s)`);

        return {
            success: true,
            count: movs.length
        };

    } catch (error) {
        console.error('❌ [EstoqueAPI] Erro ao deletar por origem:', error);
        throw error;
    }
};

/**
 * WRAPPER para compatibilidade com código antigo
 * Mantém interface de _registrarMovimentacaoEstoqueDireto
 * 
 * IMPORTANTE: Este wrapper permite migração gradual sem quebrar lancamentos-crud.js
 */
window.FazendaRP.Estoque.wrapperCompatibilidade = async function (params) {
    console.log('🔄 [WRAPPER] Redirecionando para nova API...');

    try {
        // Traduzir parâmetros antigos para novos
        return await window.FazendaRP.Estoque.registrarMovimentacao({
            itemNome: params.itemNome,
            itemId: params.itemId,
            tipo: params.tipo,
            quantidade: params.quantidade,
            custoUnitario: params.custoUnitario,
            origemTipo: params.origemTipo,
            origemId: params.origemId,
            observacao: params.observacao,
            unidade: params.unidade,
            dataOperacao: params.date || params.dataOperacao
        });

    } catch (error) {
        console.error('[WRAPPER] Erro na nova API:', error);
        throw error;
    }
};

console.log('✅ Estoque API inicializada');
console.log('   - window.FazendaRP.Estoque.registrarMovimentacao()');
console.log('   - window.FazendaRP.Estoque.deletarMovimentacao()');
console.log('   - window.FazendaRP.Estoque.deletarMovimentacaoPorOrigem()');
console.log('   - window.FazendaRP.Estoque.wrapperCompatibilidade()');
